import { Ray } from '@babylonjs/core/Culling/ray'; // also a side-effect import: enables getForwardRay / pickWithRay
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Scene } from '@babylonjs/core/scene';
import type { Weapon } from '@fisura/sim';

interface Decay {
  mesh: Mesh;
  dieAt: number;
}

/**
 * Client shooting feedback: per-weapon fire cadence, tracers, impact sparks, and
 * the hitmarker (driven by the server's authoritative HitConfirm — the client
 * never decides damage). Cosmetic only.
 */
export class CombatSystem {
  private readonly decays: Decay[] = [];
  private nowMs = 0;
  private lastShotMs = -Infinity;
  private hitmarkerHideAt = 0;

  private readonly tracerColor = new Color3(1, 0.85, 0.3);
  private readonly impactMat: StandardMaterial;
  private readonly flashMat: StandardMaterial;

  constructor(
    private readonly scene: Scene,
    private readonly camera: Camera,
    private readonly hitmarkerEl: HTMLElement,
  ) {
    this.impactMat = new StandardMaterial('impact-mat', scene);
    this.impactMat.disableLighting = true;
    this.impactMat.emissiveColor = new Color3(0.85, 0.85, 0.9);
    this.flashMat = new StandardMaterial('mflash-mat', scene);
    this.flashMat.disableLighting = true;
    this.flashMat.emissiveColor = new Color3(1, 0.8, 0.35);
  }

  /**
   * Advance one frame. Fires (cosmetically) if the trigger is down/queued and
   * the per-weapon cadence allows it. Returns the number of shots this frame.
   */
  tick(dtMs: number, firing: boolean, locked: boolean, tap: boolean, weapon: Weapon): number {
    this.nowMs += dtMs;
    this.expireEffects();

    if (locked && (firing || tap) && this.nowMs - this.lastShotMs >= weapon.rofMs) {
      this.fire(weapon);
      this.lastShotMs = this.nowMs;
      return 1;
    }
    return 0;
  }

  private fire(weapon: Weapon): void {
    const cam = this.camera;
    const ray = cam.getForwardRay(weapon.range);
    const right = cam.getDirection(Vector3.Right());
    const down = cam.getDirection(Vector3.Down());
    const muzzle = cam.position
      .add(right.scale(0.22))
      .add(down.scale(0.16))
      .add(ray.direction.scale(0.6));

    // One tracer per pellet (cosmetic spread for the shotgun).
    const pellets = Math.max(1, weapon.pellets);
    for (let i = 0; i < pellets; i++) {
      const dir =
        pellets === 1
          ? ray.direction
          : ray.direction
              .add(right.scale((Math.random() - 0.5) * 0.06))
              .add(cam.getDirection(Vector3.Up()).scale((Math.random() - 0.5) * 0.06))
              .normalize();
      const pickRay = cam.getForwardRay(weapon.range, undefined, cam.position);
      pickRay.direction.copyFrom(dir);
      const pick = this.scene.pickWithRay(
        pickRay,
        (m) => m.isPickable && m.isEnabled() && m.name !== 'ground',
      );
      const end =
        pick?.hit && pick.pickedPoint
          ? pick.pickedPoint
          : cam.position.add(dir.scale(weapon.range));
      this.spawnTracer(muzzle, end);
      if (pick?.hit && pick.pickedPoint) this.spawnImpact(pick.pickedPoint);
    }
  }

  /**
   * Render another player's shot (from a server Shot broadcast): a tracer from
   * their muzzle along the fire direction, plus a brief flash at the origin.
   */
  remoteShot(x: number, y: number, z: number, dx: number, dy: number, dz: number, range: number): void {
    const from = new Vector3(x, y, z);
    const dir = new Vector3(dx, dy, dz);
    const ray = new Ray(from, dir, range);
    const pick = this.scene.pickWithRay(ray, (m) => m.isPickable && m.isEnabled() && m.name !== 'ground');
    const end = pick?.hit && pick.pickedPoint ? pick.pickedPoint : from.add(dir.scale(range));
    this.spawnTracer(from, end);
    // Muzzle flash at the shooter (readable "someone fired there" cue).
    const s = MeshBuilder.CreateSphere('mflash', { diameter: 0.22, segments: 4 }, this.scene);
    s.position.copyFrom(from.add(dir.scale(0.4)));
    s.material = this.flashMat;
    s.isPickable = false;
    this.decays.push({ mesh: s, dieAt: this.nowMs + 50 });
  }

  /** Trigger the hitmarker from an authoritative server hit confirmation. */
  markServerHit(lethal: boolean): void {
    this.hitmarkerEl.classList.add('show');
    if (lethal) this.hitmarkerEl.classList.add('kill');
    this.hitmarkerHideAt = this.nowMs + (lethal ? 220 : 110);
  }

  private expireEffects(): void {
    if (this.hitmarkerHideAt && this.nowMs >= this.hitmarkerHideAt) {
      this.hitmarkerEl.classList.remove('show', 'kill');
      this.hitmarkerHideAt = 0;
    }
    for (let i = this.decays.length - 1; i >= 0; i--) {
      const d = this.decays[i]!;
      if (this.nowMs >= d.dieAt) {
        d.mesh.dispose();
        this.decays.splice(i, 1);
      }
    }
  }

  private spawnTracer(from: Vector3, to: Vector3): void {
    const line = MeshBuilder.CreateLines('tracer', { points: [from, to] }, this.scene);
    line.color = this.tracerColor;
    line.isPickable = false;
    this.decays.push({ mesh: line, dieAt: this.nowMs + 45 });
  }

  private spawnImpact(at: Vector3): void {
    const s = MeshBuilder.CreateSphere('impact', { diameter: 0.16, segments: 6 }, this.scene);
    s.position.copyFrom(at);
    s.material = this.impactMat;
    s.isPickable = false;
    this.decays.push({ mesh: s, dieAt: this.nowMs + 90 });
  }
}
